// NOTICE: KAPTION-INTEGRATION
// whatsapp-call-transcripts.WEB.T5 — transcripts and recording URLs per call,
// loaded on demand (the call detail, the bubble fold-out) and kept fresh by
// `kaption_call.transcript_updated`. The API comes from the call store, so
// the dev mock layer (kaptionCallMock) serves both.
//
// call-transcript-search.WEB.1 — the Calls workspace search lives here too:
// one query at a time, latest response wins.
import { reactive, readonly } from 'vue';
import { emitter as defaultEmitter } from 'shared/helpers/mitt';
import { useKaptionCallStore } from './kaptionCallStore';
import { CALL_EVENTS } from './kaptionCallEvents';
import { TRANSCRIPT_STATUS } from './kaptionCallTranscriptModel';

const unwrap = response => response?.data ?? response;

export const createKaptionCallTranscriptStore = ({ getApi }) => {
  const state = reactive({
    // call id → {loading, error, data (raw API JSON), loadedAt}
    transcripts: {},
    // call id → {loading, url, expiresAt, forbidden, error}
    recordings: {},
    retrying: {},
    search: {
      query: '',
      filters: {},
      loading: false,
      error: null,
      results: [],
      meta: {},
      seq: 0,
    },
  });

  const entry = id => state.transcripts[id] || null;

  const load = async (id, { force = false } = {}) => {
    if (!id) return null;
    const known = entry(id);
    if (known?.loading) return known.data;
    if (known?.data && !force) return known.data;
    state.transcripts[id] = { ...(known || {}), loading: true, error: null };
    try {
      const data = unwrap(await getApi().transcript(id));
      state.transcripts[id] = { loading: false, error: null, data };
      return data;
    } catch (error) {
      const status = error?.response?.status;
      state.transcripts[id] = {
        ...(known || {}),
        loading: false,
        error: status === 404 ? 'not_found' : 'failed',
      };
      return null;
    }
  };

  // API.2 — a short-lived signed URL; refetched when it is about to expire.
  const loadRecording = async (id, { force = false } = {}) => {
    if (!id || !getApi().recording) return null;
    const known = state.recordings[id];
    const fresh =
      known?.url &&
      (!known.expiresAt || Date.parse(known.expiresAt) - Date.now() > 30000);
    if (!force && (known?.loading || fresh || known?.forbidden))
      return known?.url || null;
    state.recordings[id] = { ...(known || {}), loading: true, error: null };
    try {
      const data = unwrap(await getApi().recording(id));
      state.recordings[id] = {
        loading: false,
        url: data?.url || null,
        expiresAt: data?.expires_at || null,
        forbidden: false,
        error: null,
      };
      return data?.url || null;
    } catch (error) {
      const status = error?.response?.status;
      state.recordings[id] = {
        loading: false,
        url: null,
        forbidden: status === 403,
        error: status === 403 ? null : 'failed',
      };
      return null;
    }
  };

  const retry = async id => {
    state.retrying[id] = true;
    try {
      await getApi().retryTranscript(id);
      const known = entry(id);
      if (known?.data)
        state.transcripts[id] = {
          ...known,
          data: { ...known.data, status: TRANSCRIPT_STATUS.PROCESSING },
        };
      return true;
    } catch (e) {
      return false;
    } finally {
      state.retrying[id] = false;
    }
  };

  // EVENTS.2 — a transcript this tab has open is refetched; others only
  // carry the new status until someone opens them.
  const onTranscriptUpdated = payload => {
    const id = payload?.call_id ?? payload?.id;
    const known = id && entry(id);
    if (!known) return;
    if (known.data && payload.transcript_status)
      state.transcripts[id] = {
        ...known,
        data: { ...known.data, status: payload.transcript_status },
      };
    load(id, { force: true });
  };

  // call-transcript-search.WEB.1 — debounced by the caller.
  const search = async (query, filters = {}) => {
    state.search.seq += 1;
    const seq = state.search.seq;
    state.search.query = query;
    state.search.filters = { ...filters };
    if (!String(query || '').trim()) {
      Object.assign(state.search, {
        loading: false,
        error: null,
        results: [],
        meta: {},
      });
      return [];
    }
    state.search.loading = true;
    state.search.error = null;
    try {
      const data = unwrap(await getApi().search({ q: query, ...filters }));
      if (seq !== state.search.seq) return null;
      Object.assign(state.search, {
        loading: false,
        results: data?.results || [],
        meta: data?.meta || {},
      });
      return state.search.results;
    } catch (e) {
      if (seq !== state.search.seq) return null;
      Object.assign(state.search, {
        loading: false,
        error: 'busy',
        results: [],
      });
      return null;
    }
  };

  const reset = () => {
    state.transcripts = {};
    state.recordings = {};
    state.retrying = {};
  };

  return {
    state: readonly(state),
    entry,
    load,
    loadRecording,
    retry,
    onTranscriptUpdated,
    search,
    reset,
  };
};

export const subscribeKaptionTranscriptEvents = (
  store,
  emitter = defaultEmitter
) => {
  const handler = payload => store.onTranscriptUpdated(payload);
  emitter.on(CALL_EVENTS.TRANSCRIPT_UPDATED, handler);
  return () => emitter.off(CALL_EVENTS.TRANSCRIPT_UPDATED, handler);
};

let singleton = null;

export const useKaptionCallTranscripts = () => {
  if (!singleton) {
    const calls = useKaptionCallStore();
    singleton = createKaptionCallTranscriptStore({ getApi: () => calls.api() });
    subscribeKaptionTranscriptEvents(singleton);
  }
  return singleton;
};
